import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'redux/typings';
import { appletToQuestApplet } from 'helpers/AppletHelpers';
import { QuizQuestion } from 'models/Applet';
import { QuestApplet } from 'models/QuestApplet';
import { selectAppletsContentful } from 'modules/loading-screen/contentful/applets/selectors';
import { QuestPortfolio } from 'models/QuestPortfolio';
import { IMAGE_MIMETYPE, VIDEO_MIMETYPE } from 'helpers/FileHelpers';
import { SORT_TYPE } from 'modules/profile/typings.d';
import { alphaDirectionSelect, sortModeSelect } from 'modules/profile/selectors';

const selectQuestApplets = (state: RootState) => state.applets;

type Group<T> = { header: string; items: T[] };

export const selectAvailableApplets = createSelector(
  selectQuestApplets,
  (questApplets) => questApplets.list,
);
export const selectIsGettingQuestApplets = createSelector(
  selectQuestApplets,
  (questApplets) => questApplets.isLoadingQuestApplets,
);

export const selectAppletsFilledStateSelector = createSelector(
  selectQuestApplets,
  (questApplets) => ({
    appletsActiveQuestFilled: questApplets.appletsActiveQuestFilled,
    appletsRestQuestFilled: questApplets.appletsRestQuestFilled,
  }),
);

export const selectSelectedAppletIdx = createSelector(
  selectQuestApplets,
  (questApplets) => questApplets.selectedIdx,
);

export const selectSelectedApplet = createSelector(
  selectAvailableApplets,
  selectSelectedAppletIdx,
  (appletsList, appletIdx) =>
    appletIdx !== undefined ? appletsList[appletIdx] : undefined,
);

export const selectQuestQuizQuestion = (
  quizContentfulId: string,
  quizQuestionContentfulId: string,
) =>
  createSelector(selectAvailableApplets, (questPortfolios) => {
    let quizQuestion: QuizQuestion | undefined;
    for (const quest of questPortfolios) {
      const questApplet = quest.applets.find(
        (app) => app.contentfulId === quizContentfulId,
      );
      quizQuestion = questApplet?.quizQuestions?.find(
        (question) =>
          question.quizQuestionContentfulId === quizQuestionContentfulId,
      );
      if (quizQuestion) {
        break;
      }
    }

    return quizQuestion;
  });


export const selectActiveQuestPortfolioApplets = createSelector(
  selectQuestApplets,
  selectAppletsContentful,
  (questApplets, appletsContentful) => {
    const applets = questApplets.list.filter(quest => quest.isActiveQuest).flatMap((portfolio) => portfolio.applets);
    return applets.map((applet) => appletToQuestApplet(applet, appletsContentful[applet.contentfulId]));
  },
);

export const hasMinimunAppletsCompletedForShowcase = createSelector(
  selectActiveQuestPortfolioApplets,
  (applets) => Boolean(applets.length >= 2))



export const selectQuestPortfolioApplets = createSelector(
  selectQuestApplets,
  selectAppletsContentful,
  (questApplets, appletsContentful) => {
    const applets = questApplets.list.flatMap((portfolio) => portfolio.applets);
    return applets.map((applet) => appletToQuestApplet(applet, appletsContentful[applet.contentfulId]));
  },
);

export const selectAppletsImageToAddShowcase = createSelector(
  selectQuestPortfolioApplets,
  (questPortfolioApplets) =>
    questPortfolioApplets.filter(
      (applet) =>
        applet.appletType === 'upload-image'
        || (applet.appletType === 'upload-file' && applet.fileType === IMAGE_MIMETYPE)
        || (applet.appletType === 'generic-upload-file' && applet.fileType === IMAGE_MIMETYPE)))

export const selectExistsAppletImagesToAddShowcase = createSelector(
  selectAppletsImageToAddShowcase,
  (applets) => Boolean(applets.length))



export const selectAppletsVideoToAddShowcase = createSelector(
  selectQuestPortfolioApplets,
  (questPortfolioApplets) =>
    questPortfolioApplets.filter(
      (applet) =>
        applet.appletType === 'upload-video'
        || (applet.appletType === 'upload-file' && applet.fileType === VIDEO_MIMETYPE)
        || (applet.appletType === 'generic-upload-file' && applet.fileType === VIDEO_MIMETYPE)))

export const selectExistsAppletVideosToAddShowcase = createSelector(
  selectAppletsVideoToAddShowcase,
  (applets) => Boolean(applets.length))



export const selectAllAppletsMap = createSelector(
  selectQuestPortfolioApplets,
  (applets) =>
    applets.reduce(
      (acc, app) => ({
        ...acc,
        [app.contentfulId]: app,
      }),
      {} as Record<string, QuestApplet>,
    ),
);

export const selectQuestChallengeApplet = (appletId: string) =>
  createSelector(selectAllAppletsMap, (appletsMap) => appletsMap[appletId]);

export const selectAvailableAppletsLength = createSelector(
  selectAvailableApplets,
  (questApplets) =>
    questApplets.flatMap(
      (questPortfolioApplet: QuestPortfolio) => questPortfolioApplet.applets,
    ).length || 0,
);

export const selectAppletsFromAllUserQuests = createSelector(
  selectAvailableApplets,
  (applets) => applets.flatMap((quest) => quest.applets)
);


export const selectLastAppletModifiedById = (appletId: string) =>
  createSelector(
    selectAppletsFromAllUserQuests,
    (applets) => {
      const filteredApplets = applets.filter((applet) => applet.contentfulId === appletId);
      const sortedApplets = filteredApplets.sort((a, b) => {
        const dateA = new Date(a.updatedAt).getTime();
        const dateB = new Date(b.updatedAt).getTime();
        return dateA + dateB;
      });

      return sortedApplets[0];
    }
  );

const getGroupLabel = (appletType: string, fileType?: string): string => {
  const isImage =
    !!fileType &&
    (fileType === IMAGE_MIMETYPE || fileType.startsWith?.('image/'));
  const isVideo =
    !!fileType &&
    (fileType === VIDEO_MIMETYPE || fileType.startsWith?.('video/'));

  switch (appletType) {
    case 'decision-tree':
      return 'Decision Tree';
    case 'inline-text':
      return 'Fill-in-the-Blank';
    case 'madlib':
      return 'Madlib';
    case 'random-selection':
      return 'Random Selection';
    case 'rank':
      return 'Rank';
    case 'rich-text-entry':
    case 'generic-rich-text':
      return 'Rich-Text';
    case 'quiz':
      return 'Quiz';

    // Casos de upload “puros”
    case 'upload-image':
      return 'Uploaded Image';
    case 'upload-video':
      return 'Uploaded Video';

    // Casos genéricos: decidir por fileType
    case 'upload-file':
    case 'generic-upload-file':
      if (isImage) return 'Uploaded Image';
      if (isVideo) return 'Uploaded Video';
      return 'Uploaded File';

    case 'group-applet':
    case 'add-applet':
      return 'Other';
    default:
      return 'Other';
  }
};

const TYPE_ORDER = [
  'Decision Tree',
  'Fill-in-the-Blank',
  'Madlib',
  'Random Selection',
  'Rank',
  'Rich-Text',
  'Quiz',
  'Uploaded Image',
  'Uploaded Video',
  'Uploaded File',
  'Other',
];

export const selectGroupedApplets = createSelector(
  [selectAvailableApplets, selectAppletsContentful, sortModeSelect, alphaDirectionSelect],
  (questPortfolios, appletsContentful, sortMode, alphaDirection): Group<QuestApplet>[] => {
    const enriched = questPortfolios.flatMap(qp =>
      qp.applets.map(raw => {
        const qa = appletToQuestApplet(raw, appletsContentful[raw.contentfulId]);
        return {
          ...qa,
          questTitle: qp.title,
          createdAtDate: new Date(raw.createdAt),
          typeUpper: qa.appletType.toUpperCase(),
          firstLetter: (() => {
            const match = qa.title.match(/[A-Z]/i);
            return match ? match[0].toUpperCase() : 'A';
          })(),
          groupLabel: getGroupLabel(qa.appletType, qa.fileType),
        };
      })
    );


    switch (sortMode) {
      case SORT_TYPE.DATE: {
        const monthMap = enriched.reduce((acc, cur) => {
          const d = cur.createdAtDate;
          const header = d.toLocaleString('en-US', {
            month: 'long',
            year: 'numeric'
          });
          const sortKey = d.getFullYear() * 100 + (d.getMonth() + 1);
          if (!acc[header]) {
            acc[header] = { header, items: [] as typeof enriched, sortKey };
          }
          acc[header].items.push(cur);
          return acc;
        }, {} as Record<string, { header: string; items: typeof enriched; sortKey: number }>);

        return Object.values(monthMap)
          .sort((a, b) => b.sortKey - a.sortKey)
          .map(({ header, items }) => ({
            header,
            items: items.sort((a, b) => b.createdAtDate.getTime() - a.createdAtDate.getTime())
          }));
      }

      case SORT_TYPE.TYPE: {
        const groupedByType = enriched.reduce((acc, cur) => {
          const key = cur.groupLabel;
          if (!acc[key]) acc[key] = { header: key, items: [] };
          acc[key].items.push(cur);
          return acc;
        }, {} as Record<string, Group<typeof enriched[number]>>);

        return Object.values(groupedByType)
          .map(g => ({
            header: g.header,
            items: g.items.sort((a, b) => b.createdAtDate.getTime() - a.createdAtDate.getTime()),
          }))
          .sort((a, b) => TYPE_ORDER.indexOf(a.header) - TYPE_ORDER.indexOf(b.header));
      }

      case SORT_TYPE.ALPHA: {
        const grouped = Object.values(
          enriched.reduce((acc, cur) => {
            if (!acc[cur.firstLetter]) acc[cur.firstLetter] = { header: cur.firstLetter, items: [] };
            acc[cur.firstLetter].items.push(cur);
            return acc;
          }, {} as Record<string, Group<typeof enriched[number]>>)
        ).map(g => ({
          header: g.header,
          items: g.items.sort((a, b) => b.createdAtDate.getTime() - a.createdAtDate.getTime())
        }));

        return grouped.sort((a, b) =>
          alphaDirection === 'asc'
            ? a.header.localeCompare(b.header)
            : b.header.localeCompare(a.header)
        );
      }

      case SORT_TYPE.QUEST:
      default:
        return questPortfolios.map(qp => ({
          header: qp.title,
          items: qp.applets.map(raw =>
            appletToQuestApplet(raw, appletsContentful[raw.contentfulId])
          )
        }));
    }
  }
);

